import { useRouter } from 'next/router'
import { GetStaticPaths, GetStaticProps } from 'next'
import { getAllIntegrationsByCategory, getAllPagesWithSlug, getPageForSlug } from "../../lib/api"
import Layout from "../../components/layout";
import ErrorPage404 from "../../components/404";
import React, { useMemo } from "react";
import dynamic from "next/dynamic";
import CallToActionSmall from "../../components/v2/call-to-action-sm";
import UnifiedExperience from '../../templates/platform/unified-experience';
import Alerts from '../../templates/platform/alerts';
import QueryLanguage from '../../templates/platform/query-language';
import Pipeline from '../../templates/platform/pipeline';
import Integrations from '../../templates/platform/integrations';
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import AIDashboard from '../../templates/platform/dashboard';
import NotBook from '../../templates/platform/notebook';
import "../../styles/scss/platform.module.scss";
import ServiceLevelObjectives from '../../templates/platform/service-level-objectives';

const PARENT_PAGE = 'platform';

const PAGE_COMPONENTS = {
  'unified-experience': dynamic(() => import('../../templates/platform/unified-experience')),
  'alerts': dynamic(() => import('../../templates/platform/alerts')),
  'query-language': dynamic(() => import('../../templates/platform/query-language')),
  'observability-pipeline': dynamic(() => import('../../templates/platform/pipeline')),
  'integrations': dynamic(() => import('../../templates/platform/integrations')),
};

const CommonPageTemplate = dynamic(() => import('../../templates/common'));


export default function PlatformPage({ pageData, integrationPageData }) {

  const router = useRouter();

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  const page = pageData?.pages?.edges?.[0]?.node;

  if (!page?.slug) {
    return <ErrorPage404 />;
  }

  const PageComponent = useMemo(() => PAGE_COMPONENTS[page.slug] || CommonPageTemplate, [page.slug]);

  let component
  switch (page?.slug) {
    case "unified-experience":
      component = <UnifiedExperience />;
      break;
    case "alerts":
      component = <Alerts />;
      break;

    case "query-language":
      component = <QueryLanguage />;
      break;

    case "observability-pipeline":
      component = <Pipeline />;
      break;

    case "integrations":
      component = <Integrations integrationPageData={integrationPageData} />;
      break;

    case "dashboard":
      component = <AIDashboard />;
      break;

    case "notebook":
      component = <NotBook />;
      break;

    case "service-level-objectives":
      component = <ServiceLevelObjectives />;
      break;

    default:
      component = <CommonPageTemplate pageData={pageData} />;
  }

  return (
    <>
      <Layout isHomePage={page?.isFrontPage} pageTitle={page?.title}
        uri={page?.uri}
        metaTitle={page?.seo?.title}
        metaDesc={page?.seo?.metaDesc}
        ogImage={page?.featuredImage?.node?.sourceUrl}
        metaRobotsNoindex={page?.seo?.metaRobotsNoindex}
        metaRobotsNofollow={page?.seo?.metaRobotsNofollow}>
        <div className={`middle-wrapper site-page ${PARENT_PAGE} ${page.slug}-page`}>
          {component}
          <CallToActionSmall />
        </div>
      </Layout>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  previewData,
  locale,
}) => {
  try {
    if (!params?.slug) {
      return {
        notFound: true,
      };
    }

    const data = await getPageForSlug(params?.slug, PARENT_PAGE)
    const integrationData = await getAllIntegrationsByCategory()
    let page = data?.pages?.edges?.[0]?.node

    if (!page) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        pageData: data,
        integrationPageData: integrationData,
        pageTitle: page?.seo?.title,
        description: page?.seo?.metaDesc,
        ogImage: page?.featuredImage?.node?.sourceUrl || null,
        uri: page?.uri,
        metaTitle: page?.seo?.title,
        metaDesc: page?.seo?.metaDesc,
        metaRobotsNoindex: page?.seo?.metaRobotsNoindex,
        metaRobotsNofollow: page?.seo?.metaRobotsNofollow,
        ...(await serverSideTranslations(locale ?? 'en', ['common', 'header', 'footer', 'home'], nextI18NextConfig)),
      },
      // revalidate: 10,
    }
  } catch (error) {
    console.error('Error in platform getStaticProps:', error);
    return {
      notFound: true,
    };
  }
}


export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  try {
    const allPages = await getAllPagesWithSlug()

    if (!allPages || !allPages.edges) {
      console.warn('No pages found or invalid response from getAllPagesWithSlug');
      return {
        paths: [],
        fallback: false,
      };
    }

    const pathArr: { params: { slug: string }, locale?: string }[] = []
    allPages.edges.forEach((item) => {
      if (item?.node?.parent?.node?.slug === PARENT_PAGE && item?.node?.slug) {
        if (locales && locales.length > 0) {
          locales.forEach((locale) => {
            pathArr.push({
              params: { slug: item.node.slug },
              locale,
            })
          })
        } else {
          pathArr.push({
            params: { slug: item.node.slug },
          })
        }
      }
    })

    console.log(`Generated ${pathArr.length} valid platform paths`);

    return {
      paths: pathArr,
      fallback: false,
    }
  } catch (error) {
    console.error('Error in platform getStaticPaths:', error);
    return {
      paths: [],
      fallback: false,
    }
  }
}


